import React from 'react';
import { X, CheckCircle } from '@phosphor-icons/react';

// UpgradeModal renders inside AppContext.Provider which is outside RuntimeProvider.
// upgrade.* keys are not yet in translations.js so useRuntime would always return
// the fallback anyway. This local t() replicates that behavior without the context dep.
function t(key, fallback, vars) {
  if (!fallback) return key;
  if (!vars) return fallback;
  return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) => String(vars[k] ?? ''));
}

const PLAN_DETAILS = {
  starter: {
    name: 'Starter',
    price: '$19',
    period: '/month',
    accent: 'text-gray-800',
    bg: 'bg-gray-50',
    features: [
      'Unlimited synced products',
      '1,500 AI credits/month',
      'Translations in 8 languages',
      'Image optimization',
      'Batch operations',
      'Basic Google Search Console',
      'JSON-LD Structured Data',
      'Sitemap management + Catalog analysis',
    ],
  },
  pro: {
    name: 'Pro',
    price: '$36',
    period: '/month',
    accent: 'text-[#002FA7]',
    bg: 'bg-[#002FA7]/5',
    badge: '⭐ Most Popular',
    features: [
      '5,000 synced products',
      '5,000 AI credits/month',
      'SEO Worklist + estimated recoverable clicks',
      'AI Fix SEO applicable in 1 click',
      'Full Internal Link Audit',
      'Canonical Audit + Robots.txt + LLMs.txt',
      'JSON-LD Structured Data (Rich Results)',
      'Redirect Manager + 404 Monitor',
    ],
  },
  elite: {
    name: 'Elite',
    price: '$79',
    period: '/month',
    accent: 'text-gray-800',
    bg: 'bg-gray-50',
    features: [
      'Unlimited AI credits',
      'Priority processing',
      'Advanced batch (500+ products)',
      'Early access to new features',
      'Advanced priority support',
    ],
  },
};

const REASON_COPY = {
  credits: { icon: '⚡', title: 'AI credits exhausted', body: 'You have reached the monthly limit of your plan. Upgrade to continue generating content.' },
  products: { icon: '📦', title: 'Catalog limit reached', body: 'Your Shopify catalog exceeds the current plan limit. Upgrade to sync all products.' },
  feature: { icon: '⭐', title: 'Premium Feature', body: 'This feature is available on higher plans.' },
};

const FEATURE_NAMES = {
  translations: 'Multilingual Translations',
  images: 'Image Optimization',
  batch: 'Batch Operations',
  gsc: 'Google Search Console',
  gsc_pro: 'Advanced SEO Pro',
  gsc_worklist: 'SEO Worklist',
  gsc_ai_fix: 'AI Fix SEO 1 Click',
  gsc_link_audit: 'Internal Link Audit',
  gsc_canonical: 'Canonical Audit',
  gsc_robots: 'Robots.txt Manager',
  gsc_llms: 'LLMs.txt Manager',
  gsc_jsonld: 'JSON-LD Structured Data',
};

export function UpgradeModal({ open, reason = 'feature', target = 'starter', feature, onClose }) {
  if (!open) return null;

  const plan = PLAN_DETAILS[target] || PLAN_DETAILS.starter;
  const copy = REASON_COPY[reason] || REASON_COPY.feature;
  const featureName = feature ? (FEATURE_NAMES[feature] || feature) : null;

  const reasonTitle = t(`upgrade.reason_${reason}_title`, copy.title);
  const reasonBody  = t(`upgrade.reason_${reason}_body`,  copy.body);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-sm overflow-hidden">

        {/* Header */}
        <div className={`px-6 pt-6 pb-4 ${plan.bg}`}>
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
          {plan.badge && (
            <span className="inline-block text-[10px] font-bold bg-[#002FA7] text-white px-2 py-0.5 rounded-sm mb-2 tracking-wide">
              {t('upgrade.popular', plan.badge)}
            </span>
          )}
          <div className="text-3xl mb-2">{copy.icon}</div>
          <h2 className="text-lg font-bold text-gray-900">
            {featureName ? featureName : reasonTitle}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {featureName
              ? t('upgrade.feature_available', '{{feature}} is available from the {{plan}} plan.', { feature: featureName, plan: plan.name })
              : reasonBody}
          </p>
        </div>

        {/* Plan Details */}
        <div className="px-6 py-4 border-t">
          <div className="flex items-baseline justify-between mb-3">
            <span className={`text-xl font-bold ${plan.accent}`}>{t('upgrade.plan_header', 'Plan')} {plan.name}</span>
            <span>
              <span className="text-2xl font-bold text-gray-900">{plan.price}</span>
              <span className="text-sm text-gray-400">{t('upgrade.period', plan.period)}</span>
            </span>
          </div>
          <ul className="space-y-1.5">
            {plan.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle size={14} className="text-emerald-500 shrink-0" weight="fill" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="px-6 pb-6">
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('aio:navigate', { detail: { page: 'settings' } }));
              onClose();
            }}
            className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-semibold rounded hover:bg-[#001F7A] transition-colors">
            {t('upgrade.cta', 'Upgrade to {{name}} — {{price}}{{period}}', { name: plan.name, price: plan.price, period: t('upgrade.period', plan.period) })}
          </button>
          <button onClick={onClose} className="w-full mt-2 text-xs text-gray-400 hover:text-gray-600 py-1 transition-colors">
            {t('upgrade.dismiss', 'Not now')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UpgradeModal;
